import { useRef, useState } from 'react';
import { ChromePicker } from 'react-color';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

interface Props {
  name: string;
  helperText?: string;
  type?: string;
  label?: string;
  selectedColor: string;
  onColorChange: (color: string) => void;
  [key: string]: any;
}

export default function RHFTextFieldWithColor({
  name,
  helperText,
  label,
  selectedColor,
  onColorChange,
  ...props
}: Props) {
  const theme = useTheme();
  const { control, setValue } = useFormContext();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const textFieldRef = useRef<HTMLTextAreaElement>(null);

  // 색상 선택 핸들러
  const handleColorClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // 색상 선택 닫기 핸들러
  const handleColorClose = () => {
    setAnchorEl(null);
  };

  // 값 업데이트 함수
  const updateValue = (text: string) => {
    setValue(name, {
      text,
      color: selectedColor,
    });
  };

  const defaultColors = [
    theme.palette.common.black,
    theme.palette.common.white,
    theme.palette.primary.main,
    theme.palette.error.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.grey[600],
  ];

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // 색상 업데이트
        const updateColor = (color: string) => {
          const currentValue = field.value || { text: '', color: '' };
          setValue(name, {
            ...currentValue,
            color: color,
          });
          onColorChange?.(color);
        };

        // 줄바꿈 핸들러 추가
        const handleNewLine = () => {
          const textarea = textFieldRef.current;
          if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const currentValue = field.value?.text || '';

            // 현재 커서 위치에 줄바꿈 삽입
            const newText = currentValue.substring(0, start) + '\n' + currentValue.substring(end);

            // 업데이트
            setValue(name, {
              text: newText,
              color: selectedColor,
            });

            // 커서 위치 조정
            setTimeout(() => {
              textarea.selectionStart = textarea.selectionEnd = start + 1;
              textarea.focus();
            }, 0);
          }
        };
        return (
          <Box>
            <TextField
              {...field}
              value={field.value?.text || ''}
              label={label}
              inputRef={textFieldRef}
              fullWidth
              multiline
              size={'small'}
              error={!!error}
              helperText={error ? error?.message : helperText}
              onChange={(e) => {
                updateValue(e.target.value);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Chip
                      label="줄바꿈"
                      size="small"
                      onClick={handleNewLine}
                      sx={{
                        mr: 1,
                        height: '24px',
                        '& .MuiChip-label': {
                          px: 1,
                        },
                      }}
                    />
                    <IconButton
                      onClick={handleColorClick}
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: field.value?.color || selectedColor,
                        '&:hover': { bgcolor: field.value?.color || selectedColor },
                        border: '1px solid #ddd',
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              {...props}
            />
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleColorClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
            >
              <Box sx={{ p: 2 }}>
                <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                  {defaultColors.map((color) => (
                    <IconButton
                      key={color}
                      onClick={() => {
                        updateColor(color);
                        handleColorClose();
                      }}
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: color,
                        '&:hover': { bgcolor: color },
                        border: '1px solid #ddd',
                      }}
                    />
                  ))}
                </Box>
                <CustomChromePicker
                  color={selectedColor}
                  onChange={(color) => {
                    updateColor(color.hex);
                  }}
                  disableAlpha
                />
                <Typography color="text.secondary" variant="caption">
                  {selectedColor}
                </Typography>
              </Box>
            </Popover>
          </Box>
        );
      }}
    />
  );
}

// ChromePicker의 props 타입 정의
interface ChromePickerProps {
  color: string;
  onChange: (color: { hex: string }) => void;
  disableAlpha?: boolean;
}

// ChromePicker를 함수형 컴포넌트로 래핑
const CustomChromePicker = ({ color, onChange, disableAlpha = true }: ChromePickerProps) => (
  <ChromePicker color={color} onChange={onChange} disableAlpha={disableAlpha} />
);
